/**
 * UI controls for the free fall simulation
 * Manages sliders, buttons, and other user inputs
 */

export interface ControlCallbacks {
  onPlay: () => void;
  onReset: () => void;
  onInitial: () => void;
  onLeftVelocityChange: (value: number) => void;
  onLeftAngleChange: (value: number) => void;
  onAnimationSpeedChange: (value: number) => void;
  onShowGuidesChange: (show: boolean) => void;
}

export class Controls {
  private leftVelocitySlider: HTMLInputElement;
  private leftVelocityValue: HTMLElement;
  private leftAngleSlider: HTMLInputElement;
  private leftAngleValue: HTMLElement;
  private animationSpeedSlider: HTMLInputElement;
  private animationSpeedValue: HTMLElement;
  private showGuidesCheckbox: HTMLInputElement;
  private playBtn: HTMLButtonElement;
  private resetBtn: HTMLButtonElement;
  private initialBtn: HTMLButtonElement;
  private sliders: HTMLInputElement[];

  constructor(callbacks: ControlCallbacks) {
    // Get all DOM elements
    this.leftVelocitySlider = this.getElement<HTMLInputElement>('#leftVelocity');
    this.leftVelocityValue = this.getElement<HTMLElement>('#leftVelocityValue');
    this.leftAngleSlider = this.getElement<HTMLInputElement>('#leftAngle');
    this.leftAngleValue = this.getElement<HTMLElement>('#leftAngleValue');
    this.animationSpeedSlider = this.getElement<HTMLInputElement>('#animationSpeed');
    this.animationSpeedValue = this.getElement<HTMLElement>('#animationSpeedValue');
    this.showGuidesCheckbox = this.getElement<HTMLInputElement>('#showGuides');
    this.playBtn = this.getElement<HTMLButtonElement>('#playBtn');
    this.resetBtn = this.getElement<HTMLButtonElement>('#resetBtn');
    this.initialBtn = this.getElement<HTMLButtonElement>('#initialBtn');

    // Store all sliders for easy access
    this.sliders = [
      this.leftVelocitySlider,
      this.leftAngleSlider,
      this.animationSpeedSlider
    ];

    // Set up event listeners for sliders (with auto-reset on change)
    this.leftVelocitySlider.addEventListener('input', () => {
      const value = parseFloat(this.leftVelocitySlider.value);
      this.leftVelocityValue.textContent = `${value.toFixed(1)} m/s`;
      callbacks.onLeftVelocityChange(value);
      callbacks.onReset(); // Auto-reset simulation when slider changes
    });

    this.leftAngleSlider.addEventListener('input', () => {
      const value = parseFloat(this.leftAngleSlider.value);
      this.leftAngleValue.textContent = `${value.toFixed(0)}°`;
      callbacks.onLeftAngleChange(value);
      callbacks.onReset(); // Auto-reset simulation when slider changes
    });

    this.animationSpeedSlider.addEventListener('input', () => {
      const value = parseFloat(this.animationSpeedSlider.value);
      this.animationSpeedValue.textContent = `${value.toFixed(1)}x`;
      callbacks.onAnimationSpeedChange(value);
    });

    this.showGuidesCheckbox.addEventListener('change', () => {
      callbacks.onShowGuidesChange(this.showGuidesCheckbox.checked);
    });

    this.playBtn.addEventListener('click', () => {
      callbacks.onPlay();
    });

    this.resetBtn.addEventListener('click', () => {
      callbacks.onReset();
    });

    this.initialBtn.addEventListener('click', () => {
      callbacks.onInitial();
    });

    // Initialize display values
    this.updateDisplayValues();
  }

  private getElement<T extends HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) {
      throw new Error(`Element not found: ${selector}`);
    }
    return element;
  }

  private updateDisplayValues(): void {
    this.leftVelocityValue.textContent = `${parseFloat(this.leftVelocitySlider.value).toFixed(1)} m/s`;
    this.leftAngleValue.textContent = `${parseFloat(this.leftAngleSlider.value).toFixed(0)}°`;
    this.animationSpeedValue.textContent = `${parseFloat(this.animationSpeedSlider.value).toFixed(1)}x`;
  }

  public getLeftVelocity(): number {
    return parseFloat(this.leftVelocitySlider.value);
  }

  public getLeftAngle(): number {
    return parseFloat(this.leftAngleSlider.value);
  }

  public getAnimationSpeed(): number {
    return parseFloat(this.animationSpeedSlider.value);
  }

  public getShowGuides(): boolean {
    return this.showGuidesCheckbox.checked;
  }

  public setPlayButtonToPause(): void {
    this.playBtn.textContent = 'Pause';
    this.playBtn.disabled = false;
  }

  public setPlayButtonToResume(): void {
    this.playBtn.textContent = 'Resume';
    this.playBtn.disabled = false;
  }

  public setPlayButtonToPlay(): void {
    this.playBtn.textContent = 'Play';
    this.playBtn.disabled = false;
  }

  public enablePlayButton(): void {
    this.playBtn.disabled = false;
  }

  public resetToInitialValues(): void {
    this.sliders.forEach(slider => {
      const initialValue = slider.getAttribute('data-initial');
      if (initialValue) {
        slider.value = initialValue;
      }
    });
    this.updateDisplayValues();
  }
}
